import { useEffect, useState } from "react";
import { BarChart3, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";


const emptyScenario = {
  name: "",
  client_name: "",
  pricing_model: "hourly",
  staff_count: "1",
  hours_per_week: "40",
  contract_weeks: "52",
  bill_rate: "",
  annual_contract_value: "",
  payroll_rate: "",
};


function currency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value || 0);
}


function ProjectionMetric({ label, value, caption, tone, testId }) {
  return (
    <article className="ops-metric" data-testid={testId}>
      <p className="ops-metric-label">{label}</p>
      <strong className="ops-metric-value" data-testid={`${testId}-value`}>{value}</strong>
      <span className={`ops-metric-caption is-${tone}`}>{caption}</span>
    </article>
  );
}


export default function ContractProjectionsPanel({ canManage }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [scenario, setScenario] = useState(emptyScenario);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  async function loadProjections() {
    setIsRefreshing(true);
    try {
      const response = await api.get("/projections");
      setData(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsRefreshing(false);
    }
  }

  useEffect(() => {
    loadProjections();
  }, []);

  function resetForm() {
    setEditingId("");
    setScenario(emptyScenario);
    setShowForm(false);
  }

  function scenarioPayload() {
    return {
      ...scenario,
      staff_count: Number(scenario.staff_count),
      hours_per_week: Number(scenario.hours_per_week),
      contract_weeks: Number(scenario.contract_weeks),
      bill_rate: Number(scenario.bill_rate || 0),
      annual_contract_value: Number(scenario.annual_contract_value || 0),
      payroll_rate: Number(scenario.payroll_rate || 0),
    };
  }

  async function saveScenario(event) {
    event.preventDefault();
    try {
      const payload = scenarioPayload();
      if (editingId) {
        await api.patch(`/projections/${editingId}`, payload);
      } else {
        await api.post("/projections", payload);
      }
      resetForm();
      await loadProjections();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  function editScenario(item) {
    setEditingId(item.id);
    setScenario({
      name: item.name,
      client_name: item.client_name || "",
      pricing_model: item.pricing_model,
      staff_count: String(item.staff_count),
      hours_per_week: String(item.hours_per_week),
      contract_weeks: String(item.contract_weeks),
      bill_rate: String(item.bill_rate || ""),
      annual_contract_value: String(item.annual_contract_value || ""),
      payroll_rate: String(item.payroll_rate || ""),
    });
    setShowForm(true);
  }

  async function deleteScenario(scenarioId) {
    try {
      await api.delete(`/projections/${scenarioId}`);
      setDeleteCandidate(null);
      await loadProjections();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  if (!data && !error) {
    return <section className="projection-loading" data-testid="contract-projections-loading">Loading contract outlook…</section>;
  }

  if (error && !data) {
    return <section className="projection-loading projection-error" data-testid="contract-projections-error">{error}</section>;
  }

  const { summary, scenarios } = data;
  const scenarioTotals = summary.scenario_totals || summary;
  const liveTotals = summary.live_totals || { count: 0 };
  const unpriced = summary.unpriced_live || { count: 0 };
  const hours = (value) => Number(value || 0).toLocaleString();

  const openCreate = () => {
    setEditingId("");
    setScenario(emptyScenario);
    setShowForm(true);
  };
  const field = (key) => (event) => setScenario({ ...scenario, [key]: event.target.value });

  return (
    <div className="projections-panel" data-testid="contract-projections-panel">
      <header className="ops-header">
        <div>
          <p className="ops-eyebrow">INTERNAL FINANCIAL PLANNING</p>
          <h1 className="ops-greeting">Projections</h1>
          <p className="ops-context" data-testid="projections-context">
            {summary.scenario_count} saved {summary.scenario_count === 1 ? "scenario" : "scenarios"} · {summary.live_assignment_count} live
            {summary.live_assignment_count === 1 ? " assignment" : " assignments"} outside scenarios · combined gross margin {summary.gross_margin_percent}%
          </p>
        </div>
        {canManage && (
          <div className="ops-actions">
            <button className="ops-primary-button" data-testid="create-projection-button" type="button" onClick={openCreate}>
              <Plus size={16} /> Add projection
            </button>
          </div>
        )}
      </header>

      {error && <p className="form-message error-message" data-testid="projection-error-message">{error}</p>}
      {isRefreshing && <p className="projection-refreshing" data-testid="projection-refreshing-status">Updating contract outlook…</p>}

      <section className="ops-metric-grid" data-testid="projection-metrics">
        <ProjectionMetric label="Scenario staffing hours" value={hours(scenarioTotals.annual_hours)} caption={`${summary.scenario_count} saved ${summary.scenario_count === 1 ? "scenario" : "scenarios"}`} tone="gray" testId="projection-annual-hours" />
        <ProjectionMetric label="Scenario contract revenue" value={currency(scenarioTotals.projected_revenue)} caption="Annual contract value across scenarios" tone="gray" testId="projection-revenue" />
        <ProjectionMetric label="Scenario payroll cost" value={currency(scenarioTotals.estimated_payroll_cost)} caption="Wages and burden for scenario hours" tone="gray" testId="projection-payroll-cost" />
        <ProjectionMetric label="Scenario gross margin" value={currency(scenarioTotals.gross_margin)} caption={`${scenarioTotals.gross_margin_percent}% of scenario revenue`} tone={scenarioTotals.gross_margin_percent >= 30 ? "green" : "amber"} testId="projection-gross-margin" />
      </section>

      <section className="ops-chart-grid projections-grid">
        <article className="ops-card" data-testid="projection-scenarios-card">
          <div className="ops-card-heading">
            <div>
              <h2>Saved scenarios</h2>
              <p>Each row shows staff, weekly hours per person, contract weeks and the resulting annual hours.</p>
            </div>
          </div>
          {showForm && canManage && (
            <form className="projection-form" data-testid="projection-form" onSubmit={saveScenario}>
              <div className="projection-form-heading">
                <div>
                  <h3>{editingId ? "Edit contract scenario" : "Add contract scenario"}</h3>
                  <p>Hours, pricing, and payroll assumptions create the annual forecast.</p>
                </div>
                <button aria-label="Close projection form" className="projection-icon-button" data-testid="cancel-projection-button" type="button" onClick={resetForm}>
                  <X size={17} />
                </button>
              </div>
              <label>Scenario name<input data-testid="projection-name-input" required value={scenario.name} onChange={field("name")} /></label>
              <label>Client<input data-testid="projection-client-input" value={scenario.client_name} onChange={field("client_name")} /></label>
              <label>
                Pricing model
                <select data-testid="projection-pricing-model-select" value={scenario.pricing_model} onChange={field("pricing_model")}>
                  <option value="hourly">Hourly bill rate</option>
                  <option value="annual_value">Annual contract value</option>
                </select>
              </label>
              <label>Staff count<input data-testid="projection-staff-count-input" min="1" required type="number" value={scenario.staff_count} onChange={field("staff_count")} /></label>
              <label>Hours per week<input data-testid="projection-hours-week-input" min="0" required step="0.5" type="number" value={scenario.hours_per_week} onChange={field("hours_per_week")} /></label>
              <label>Contract weeks<input data-testid="projection-contract-weeks-input" max="52" min="1" required type="number" value={scenario.contract_weeks} onChange={field("contract_weeks")} /></label>
              {scenario.pricing_model === "hourly" ? (
                <label>Bill rate / hour<input data-testid="projection-bill-rate-input" min="0" required step="0.01" type="number" value={scenario.bill_rate} onChange={field("bill_rate")} /></label>
              ) : (
                <label>Annual contract value<input data-testid="projection-annual-value-input" min="0" required step="0.01" type="number" value={scenario.annual_contract_value} onChange={field("annual_contract_value")} /></label>
              )}
              <label>Payroll cost / hour<input data-testid="projection-payroll-rate-input" min="0" required step="0.01" type="number" value={scenario.payroll_rate} onChange={field("payroll_rate")} /></label>
              <button className="button button-secondary projection-save-button" data-testid="save-projection-button">
                <BarChart3 size={16} /> {editingId ? "Update projection" : "Save projection"}
              </button>
            </form>
          )}
          <div className="projection-scenario-list" data-testid="projection-scenario-list">
            {scenarios.map((item) => (
              <article className="projection-scenario-row" data-testid={`projection-${item.id}`} key={item.id}>
                <div>
                  <strong>{item.name}</strong>
                  <span>
                    {item.client_name || "Internal scenario"} · {item.staff_count} staff · {Number(item.hours_per_week).toLocaleString()} hrs/wk each · {item.contract_weeks} wks ·{" "}
                    {item.annual_hours.toLocaleString()} hrs total
                  </span>
                </div>
                <div className="projection-scenario-value">
                  <strong>{currency(item.projected_revenue)}</strong>
                  <span>{item.gross_margin_percent}% gross margin · {currency(item.estimated_payroll_cost)} payroll</span>
                </div>
                {canManage && (
                  <div className="projection-row-actions">
                    <button aria-label={`Edit ${item.name}`} className="projection-icon-button" data-testid={`edit-projection-${item.id}`} type="button" onClick={() => editScenario(item)}>
                      <Pencil size={15} />
                    </button>
                    <button aria-label={`Delete ${item.name}`} className="projection-icon-button projection-delete-button" data-testid={`delete-projection-${item.id}`} type="button" onClick={() => setDeleteCandidate(item)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </article>
            ))}
            {!scenarios.length && (
              <p className="projection-empty-state" data-testid="projection-empty-state">Add a contract scenario to begin annual revenue and capacity planning.</p>
            )}
          </div>
          {deleteCandidate && (
            <div className="projection-delete-confirm" data-testid="projection-delete-confirmation">
              <span>Remove “{deleteCandidate.name}” from this annual outlook?</span>
              <div>
                <button className="button button-secondary" data-testid="cancel-delete-projection-button" type="button" onClick={() => setDeleteCandidate(null)}>Keep scenario</button>
                <button className="button projection-confirm-delete-button" data-testid="confirm-delete-projection-button" type="button" onClick={() => deleteScenario(deleteCandidate.id)}>Remove scenario</button>
              </div>
            </div>
          )}
        </article>

        <article className="ops-card" data-testid="projection-reconciliation-card">
          <div className="ops-card-heading">
            <div>
              <h2>How the outlook is built</h2>
              <p>Scenario totals plus priced live assignments. Unpriced work is listed, not counted.</p>
            </div>
          </div>
          <dl className="projection-reconcile" data-testid="projection-reconciliation">
            <div data-testid="projection-live-priced">
              <dt>Priced live assignments</dt>
              <dd>
                {liveTotals.count
                  ? `${liveTotals.count} · ${hours(liveTotals.annual_hours)} hrs · ${currency(liveTotals.projected_revenue)} revenue · ${currency(liveTotals.estimated_payroll_cost)} payroll · ${liveTotals.gross_margin_percent}% margin`
                  : "None — every live assignment is already covered by a saved scenario or has no billing data."}
              </dd>
            </div>
            <div data-testid="projection-live-unpriced">
              <dt>Unpriced live assignments</dt>
              <dd>
                {unpriced.count
                  ? `${unpriced.count} · ${hours(unpriced.annual_hours)} hrs · ${currency(unpriced.estimated_payroll_cost)} payroll · no committed billing, so excluded from margin until priced.`
                  : "None."}
              </dd>
            </div>
            <div className="projection-reconcile-total" data-testid="projection-combined">
              <dt>Combined outlook</dt>
              <dd>
                {hours(summary.annual_hours)} hrs · {currency(summary.projected_revenue)} revenue · {currency(summary.estimated_payroll_cost)} payroll · <strong>{summary.gross_margin_percent}% gross margin</strong>
              </dd>
            </div>
          </dl>
        </article>
      </section>
    </div>
  );
}
